import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ProductType } from "@/core";
import { formatPrice } from "@/lib";
import {
  calculateDynamicRate,
  calculateTotalPayout,
} from "@/lib/miles-calculations";
import { TransactionTypes, useDashboardStore } from "@/stores";
import { Loader2 } from "lucide-react";
import { useState } from "react";

interface EmailDialogProps {
  onEmailDetails?: () => Promise<void>;
}

export const EmailDialog = ({ onEmailDetails }: EmailDialogProps) => {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const program = useDashboardStore((state) => state.program);

  const handleClose = () => {
    setShowDetails(false);
    setOpen(false);
  };

  const handleEmailDetails = async () => {
    setIsLoading(true);
    await onEmailDetails?.();
    setIsLoading(false);
    handleClose();
  };

  if (!program) {
    return null;
  }
  const transactionType =
    program.buyOrSell === ProductType.BUY
      ? TransactionTypes.BUY
      : TransactionTypes.SELL;

  const rate = calculateDynamicRate({
    miles: program.sanitizedMiles,
    transactionType,
    sellPrices: program.sellPrices,
    buyPrices: program.buyPrices,
    fallbackRate: 1,
  });

  const { statusBonusAmount, promoCodeDiscount } = calculateTotalPayout({
    totalMiles: program.sanitizedMiles,
    dynamicRate: rate,
    loyaltyStatus: program.loyaltyStatus,
    transactionType,
    promoCode: program.promoCode,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) setShowDetails(false);

        setOpen(newOpen);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="link" className="font-semibold">
          I need more time - send me an email
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-sm max-h-screen overflow-auto">
        {!showDetails ? (
          <div className="flex flex-col gap-6">
            <DialogTitle className="text-xl font-semibold flex gap-2 items-center">
              <div className="bg-black w-fit p-2 rounded-sm">
                <img
                  src="/dashboard/emailPopup/thinking.svg"
                  alt="thinking"
                  className="h-full w-full"
                />
              </div>
              Still thinking it over?
            </DialogTitle>
            <p className="text-muted-foreground">
              No problem - we’ll email you the quote so you can review it
              anytime
            </p>
            <div className="flex flex-col gap-4">
              {/* <Button onClick={handleSecureQuote} className="w-full rounded-sm">
                Yes, I'm Ready - Secure my quote
              </Button> */}
              <Button
                onClick={() => setShowDetails(true)}
                // variant="secondary"
                className="w-full rounded-sm"
              >
                Email me the details
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left space-y-3">
              <DialogTitle className="text-xl font-semibold flex gap-2 items-center">
                <div className="bg-accent p-2 rounded-sm">
                  <img
                    src="/dashboard/emailPopup/shield.svg"
                    alt="shield"
                    className="h-full w-full"
                  />
                </div>
                Your Quote Details
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4 mt-4">
              <div className="p-4 bg-slate-200/30 rounded-sm">
                <div className="w-full flex gap-1">
                  <span>Airline:</span>

                  <div>{program.airline}</div>
                </div>
                <p>
                  Rate:{" "}
                  {formatPrice({
                    price: rate * 100,
                    fractionDigits: 2,
                  })}
                  /100 Miles
                </p>
                {program.loyaltyStatus !== null && statusBonusAmount > 0 && (
                  <div className="flex justify-between items-center">
                    <p className="font-medium ">
                      Loyalty Bonus{" "}
                      <span className="text-blue-600 font-semibold">
                        {program.loyaltyStatus?.name}
                      </span>
                    </p>
                    <span className="font-semibold text-blue-600">
                      {formatPrice({
                        price: statusBonusAmount,
                      })}
                    </span>
                  </div>
                )}
                {program.promoCode && promoCodeDiscount !== 0 && (
                  <div className="flex justify-between items-center">
                    <p className="font-medium ">
                      Promo Code{" "}
                      <span className="text-blue-600 font-semibold">
                        {program.promoCode.name}
                      </span>
                    </p>
                    <span className={`font-semibold text-blue-600`}>
                      {formatPrice({
                        price: promoCodeDiscount,
                      })}
                    </span>
                  </div>
                )}
                <p>Quantity: {program.sanitizedMiles.toLocaleString()} miles</p>
                <p>
                  Program Total:{" "}
                  {formatPrice({
                    price: program.totalPayout,
                  })}
                </p>
              </div>

              <div className="p-4 rounded text-right font-semibold">
                Total Payout:{" "}
                {formatPrice({
                  price: program.totalPayout,
                })}
              </div>
              <Button
                onClick={handleEmailDetails}
                className="text-white font-medium py-3"
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                Email Me This Offer
              </Button>
              <Button
                onClick={handleClose}
                variant="link"
                className="font-medium"
              >
                Adjust the Quote
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
